import { z } from "zod";

export const getStripeObjectIdPattern = (prefix: string) => `^${prefix}_[A-Za-z0-9_]+$`;

export const makeStripeObjectIdSchema = (prefix: string) =>
  z.string().regex(new RegExp(getStripeObjectIdPattern(prefix)), {
    error: `Expected a Stripe ${prefix} id`,
  });

export const billingCycleSchema = z.enum(["monthly", "yearly"]);
export type BillingCycle = z.infer<typeof billingCycleSchema>;
export const BILLING_CYCLES = billingCycleSchema.options;

export const getBillingCycleCreditAllowance = (
  monthlyCreditAllowance: number | null,
  billingCycle: BillingCycle | null | undefined,
): number | null => {
  if (monthlyCreditAllowance === null) {
    return null;
  }

  return billingCycle === "yearly" ? monthlyCreditAllowance * 12 : monthlyCreditAllowance;
};

export const billingPlanSourceSchema = z.enum(["manual", "stripe"]);
export type BillingPlanSource = z.infer<typeof billingPlanSourceSchema>;
export const BILLING_PLAN_SOURCES = billingPlanSourceSchema.options;

export const billingPlanStripeSyncStatusSchema = z.enum([
  "not_applicable",
  "pending",
  "synced",
  "failed",
]);
export type BillingPlanStripeSyncStatus = z.infer<typeof billingPlanStripeSyncStatusSchema>;
export const BILLING_PLAN_STRIPE_SYNC_STATUSES = billingPlanStripeSyncStatusSchema.options;

export const workspaceCreditPackStatusSchema = z.enum([
  "pending",
  "active",
  "depleted",
  "expired",
  "payment_failed",
]);
export type WorkspaceCreditPackStatus = z.infer<typeof workspaceCreditPackStatusSchema>;
export const WORKSPACE_CREDIT_PACK_STATUSES = workspaceCreditPackStatusSchema.options;

export const billingTaxBehaviorSchema = z.enum(["exclusive", "inclusive"]);
export type BillingTaxBehavior = z.infer<typeof billingTaxBehaviorSchema>;
export const BILLING_TAX_BEHAVIORS = billingTaxBehaviorSchema.options;

export const billingCurrencySchema = z.enum(["eur"]);
export type BillingCurrency = z.infer<typeof billingCurrencySchema>;
export const BILLING_CURRENCIES = billingCurrencySchema.options;
export const DEFAULT_BILLING_CURRENCY = "eur" satisfies BillingCurrency;
export const DEFAULT_BILLING_TAX_BEHAVIOR = "exclusive" satisfies BillingTaxBehavior;
export const DEFAULT_SIGNUP_FREE_RUNS = 4;
export const SIGNUP_FREE_RUN_PACK_CODE = "signup_free_runs";
export const SIGNUP_FREE_RUN_PACK_NAME = "Internal signup run grant";
export const SIGNUP_FREE_RUN_PACK_EXPIRES_AFTER_DAYS = 36_500;

export const workspaceBillingStatusSchema = z.enum([
  "incomplete",
  "incomplete_expired",
  "trialing",
  "active",
  "past_due",
  "canceled",
  "unpaid",
  "paused",
]);
export type WorkspaceBillingStatus = z.infer<typeof workspaceBillingStatusSchema>;
export const WORKSPACE_BILLING_STATUSES = workspaceBillingStatusSchema.options;
export const WORKSPACE_BILLING_CHANGEABLE_STATUSES: readonly WorkspaceBillingStatus[] = [
  "active",
  "trialing",
];
export const WORKSPACE_BILLING_RECHECKOUT_STATUSES: readonly WorkspaceBillingStatus[] = [
  "canceled",
  "incomplete_expired",
];

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const DEFAULT_FAILED_PAYMENT_GRACE_PERIOD_DAYS = 7;

export const WORKSPACE_BILLING_FAILED_PAYMENT_GRACE_STATUSES: readonly WorkspaceBillingStatus[] = [
  "past_due",
];

const toValidDate = (value: Date | string | null | undefined): Date | null => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getFailedPaymentGraceEndsAt = (input: {
  paymentFailedAt: Date | string | null | undefined;
  gracePeriodDays: number;
}): Date | null => {
  const paymentFailedAt = toValidDate(input.paymentFailedAt);
  if (!paymentFailedAt || !Number.isInteger(input.gracePeriodDays) || input.gracePeriodDays < 0) {
    return null;
  }

  return new Date(paymentFailedAt.getTime() + input.gracePeriodDays * DAY_IN_MS);
};

export const getWorkspaceBillingEntitlementState = (input: {
  status: WorkspaceBillingStatus | null | undefined;
  paymentFailedAt?: Date | string | null;
  failedPaymentGracePeriodDays: number;
  now?: Date;
}) => {
  const graceEndsAt = getFailedPaymentGraceEndsAt({
    paymentFailedAt: input.paymentFailedAt,
    gracePeriodDays: input.failedPaymentGracePeriodDays,
  });
  const now = input.now ?? new Date();
  const isFailedPaymentStatus = input.status === "past_due";
  const isFailedPaymentGraceActive = Boolean(
    isFailedPaymentStatus && graceEndsAt && now.getTime() < graceEndsAt.getTime(),
  );
  const isFailedPaymentGraceExpired = Boolean(isFailedPaymentStatus && !isFailedPaymentGraceActive);

  return {
    hasEntitlement:
      input.status === "active" || input.status === "trialing" || isFailedPaymentGraceActive,
    failedPaymentGraceEndsAt: graceEndsAt,
    isFailedPaymentGraceActive,
    isFailedPaymentGraceExpired,
  };
};

export const billingPlanEntitlementKeySchema = z.enum(["brand_limit", "monthly_credit_allowance"]);
export type BillingPlanEntitlementKey = z.infer<typeof billingPlanEntitlementKeySchema>;
export const BILLING_PLAN_ENTITLEMENT_KEYS = billingPlanEntitlementKeySchema.options;

export const billingPlanEntitlementValueTypeSchema = z.enum(["integer", "boolean", "text"]);
export type BillingPlanEntitlementValueType = z.infer<typeof billingPlanEntitlementValueTypeSchema>;
export type BillingPlanEntitlementValue = number | boolean | string;
export const BILLING_PLAN_ENTITLEMENT_VALUE_TYPES = billingPlanEntitlementValueTypeSchema.options;

export const stripeCustomerIdSchema = makeStripeObjectIdSchema("cus");
export const stripeSubscriptionIdSchema = makeStripeObjectIdSchema("sub");
export const stripeProductIdSchema = makeStripeObjectIdSchema("prod");
export const stripePriceIdSchema = makeStripeObjectIdSchema("price");
export const stripePaymentMethodIdSchema = makeStripeObjectIdSchema("pm");

export const stripeSecretKeySchema = z.string().regex(/^sk_(test|live)_[A-Za-z0-9_]+$/, {
  error: "Expected a Stripe secret key",
});

export const stripePublishableKeySchema = z.string().regex(/^pk_(test|live)_[A-Za-z0-9_]+$/, {
  error: "Expected a Stripe publishable key",
});

export const stripeWebhookSecretSchema = z.string().regex(/^whsec_[A-Za-z0-9_]+$/, {
  error: "Expected a Stripe webhook secret",
});
